import * as React from "react";
import { useInput } from "react-admin";
import {
  Typography,
  Button,
  IconButton,
  Paper,
  TextField,
  Divider,
  Collapse,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";

/**
 * StructureTab manages the nested block → section → class hierarchy.
 *
 * Data flow:
 * - Reads from `blocks`, `sections`, `classes` fields on the record
 * - Converts Firebase map format into an ordered array for editing
 * - On save, the data handler (shield/src/data/programs/index.js)
 *   converts back to the mutation input format
 */
export function StructureTab({ source }) {
  const blocksInput = useInput({ source: "blocks" });
  const sectionsInput = useInput({ source: "sections" });
  const classesInput = useInput({ source: "classes" });

  // Convert Firebase maps to ordered arrays
  const blocksObj = blocksInput.input.value || {};
  const sectionsObj = sectionsInput.input.value || {};
  const classesObj = classesInput.input.value || {};

  const sortedBlocks = React.useMemo(() => {
    return Object.values(blocksObj).sort(
      (a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)
    );
  }, [blocksObj]);

  const updateAll = (newBlocks, newSections, newClasses) => {
    blocksInput.input.onChange(newBlocks);
    sectionsInput.input.onChange(newSections);
    classesInput.input.onChange(newClasses);
  };

  const addBlock = () => {
    const tempRefId = `new_block_${Date.now()}`;
    const newBlock = {
      refId: tempRefId,
      title: "New Block",
      superscript: "",
      sortIndex: sortedBlocks.length,
      sections: [],
      classes: [],
    };
    updateAll(
      { ...blocksObj, [tempRefId]: newBlock },
      sectionsObj,
      classesObj
    );
  };

  const removeBlock = (blockRefId) => {
    const block = blocksObj[blockRefId];
    if (!block) {
      return;
    }

    const newBlocks = { ...blocksObj };
    const newSections = { ...sectionsObj };
    const newClasses = { ...classesObj };

    // Remove all sections and classes in this block
    (block.sections || []).forEach((sRefId) => {
      const section = newSections[sRefId];
      if (section) {
        (section.classes || []).forEach((cls) => {
          const classRefId = cls.refId;
          if (classRefId) {
            delete newClasses[classRefId];
          }
        });
      }
      delete newSections[sRefId];
    });
    delete newBlocks[blockRefId];

    // Re-index block sortIndexes
    const remaining = Object.values(newBlocks).sort(
      (a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)
    );
    remaining.forEach((b, idx) => {
      newBlocks[b.refId] = { ...b, sortIndex: idx };
    });

    updateAll(newBlocks, newSections, newClasses);
  };

  const updateBlockField = (blockRefId, field, value) => {
    const newBlocks = {
      ...blocksObj,
      [blockRefId]: { ...blocksObj[blockRefId], [field]: value },
    };
    updateAll(newBlocks, sectionsObj, classesObj);
  };

  const moveBlock = (blockRefId, direction) => {
    const blocks = Object.values(blocksObj).sort(
      (a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)
    );
    const idx = blocks.findIndex((b) => b.refId === blockRefId);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= blocks.length) {
      return;
    }

    [blocks[idx], blocks[newIdx]] = [blocks[newIdx], blocks[idx]];
    const newBlocks = {};
    blocks.forEach((b, i) => {
      newBlocks[b.refId] = { ...b, sortIndex: i };
    });
    updateAll(newBlocks, sectionsObj, classesObj);
  };

  const addSection = (blockRefId) => {
    const tempRefId = `new_section_${Date.now()}`;
    const block = blocksObj[blockRefId];
    const newSection = {
      refId: tempRefId,
      blockRefId,
      title: "New Section",
      durationInSeconds: 0,
      classes: [],
    };
    const newSections = { ...sectionsObj, [tempRefId]: newSection };
    const newBlocks = {
      ...blocksObj,
      [blockRefId]: {
        ...block,
        sections: [...(block.sections || []), tempRefId],
      },
    };
    updateAll(newBlocks, newSections, classesObj);
  };

  const removeSection = (blockRefId, sectionRefId) => {
    const newSections = { ...sectionsObj };
    const newClasses = { ...classesObj };
    const section = newSections[sectionRefId];

    // Remove classes in this section
    if (section) {
      (section.classes || []).forEach((cls) => {
        if (cls.refId) {
          delete newClasses[cls.refId];
        }
      });
    }
    delete newSections[sectionRefId];

    // Remove from block's section list
    const block = blocksObj[blockRefId];
    const newBlocks = {
      ...blocksObj,
      [blockRefId]: {
        ...block,
        sections: (block.sections || []).filter((s) => s !== sectionRefId),
        classes: (block.classes || []).filter(
          (c) => !section?.classes?.some((sc) => sc.refId === c)
        ),
      },
    };

    updateAll(newBlocks, newSections, newClasses);
  };

  const moveSection = (blockRefId, sectionRefId, direction) => {
    const block = blocksObj[blockRefId];
    const sectionList = [...(block.sections || [])];
    const idx = sectionList.indexOf(sectionRefId);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= sectionList.length) {
      return;
    }

    [sectionList[idx], sectionList[newIdx]] = [sectionList[newIdx], sectionList[idx]];
    const newBlocks = {
      ...blocksObj,
      [blockRefId]: { ...block, sections: sectionList },
    };
    updateAll(newBlocks, sectionsObj, classesObj);
  };

  const updateSectionTitle = (sectionRefId, title) => {
    const newSections = {
      ...sectionsObj,
      [sectionRefId]: { ...sectionsObj[sectionRefId], title },
    };
    updateAll(blocksObj, newSections, classesObj);
  };

  const addClassToSection = (blockRefId, sectionRefId) => {
    const tempRefId = `new_class_${Date.now()}`;
    const section = sectionsObj[sectionRefId];
    const block = blocksObj[blockRefId];

    const newClassEntry = {
      refId: tempRefId,
      classId: 0,
      blockRefId,
      sectionRefId,
      sortIndex: Object.keys(classesObj).length,
    };

    const newClasses = { ...classesObj, [tempRefId]: newClassEntry };
    const newSections = {
      ...sectionsObj,
      [sectionRefId]: {
        ...section,
        classes: [
          ...(section.classes || []),
          { id: 0, refId: tempRefId },
        ],
      },
    };
    const newBlocks = {
      ...blocksObj,
      [blockRefId]: {
        ...block,
        classes: [...(block.classes || []), tempRefId],
      },
    };

    updateAll(newBlocks, newSections, newClasses);
  };

  const removeClassFromSection = (blockRefId, sectionRefId, classRefId) => {
    const section = sectionsObj[sectionRefId];
    const block = blocksObj[blockRefId];
    const newClasses = { ...classesObj };
    delete newClasses[classRefId];

    const newSections = {
      ...sectionsObj,
      [sectionRefId]: {
        ...section,
        classes: (section.classes || []).filter((c) => c.refId !== classRefId),
      },
    };
    const newBlocks = {
      ...blocksObj,
      [blockRefId]: {
        ...block,
        classes: (block.classes || []).filter((c) => c !== classRefId),
      },
    };

    updateAll(newBlocks, newSections, newClasses);
  };

  const moveClassInSection = (sectionRefId, classRefId, direction) => {
    const section = sectionsObj[sectionRefId];
    const classList = [...(section.classes || [])];
    const idx = classList.findIndex((c) => c.refId === classRefId);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= classList.length) {
      return;
    }

    [classList[idx], classList[newIdx]] = [classList[newIdx], classList[idx]];
    const newSections = {
      ...sectionsObj,
      [sectionRefId]: { ...section, classes: classList },
    };
    updateAll(blocksObj, newSections, classesObj);
  };

  const updateClassId = (sectionRefId, classRefId, classId) => {
    const numericId = parseInt(classId, 10) || 0;

    // Update in classes map
    const newClasses = {
      ...classesObj,
      [classRefId]: { ...classesObj[classRefId], classId: numericId },
    };

    // Update in section's classes array
    const section = sectionsObj[sectionRefId];
    const newSections = {
      ...sectionsObj,
      [sectionRefId]: {
        ...section,
        classes: (section.classes || []).map((c) =>
          c.refId === classRefId ? { ...c, id: numericId } : c
        ),
      },
    };

    updateAll(blocksObj, newSections, newClasses);
  };

  return (
    <div style={{ maxWidth: 800 }}>
      <Typography variant="h6" gutterBottom>
        Program Structure
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Organize the program into blocks, sections, and classes. Each section
        contains classes that make up a day&apos;s lesson.
      </Typography>

      {sortedBlocks.map((block, blockIdx) => (
        <Paper
          key={block.refId}
          variant="outlined"
          style={{ padding: 16, marginBottom: 16 }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Typography variant="subtitle1" style={{ fontWeight: "bold", flex: 1 }}>
              Block {blockIdx + 1}
            </Typography>
            <IconButton
              size="small"
              onClick={() => moveBlock(block.refId, -1)}
              disabled={blockIdx === 0}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => moveBlock(block.refId, 1)}
              disabled={blockIdx === sortedBlocks.length - 1}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => removeBlock(block.refId)}
              color="secondary"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>

          <TextField
            label="Block Title"
            value={block.title || ""}
            onChange={(e) =>
              updateBlockField(block.refId, "title", e.target.value)
            }
            fullWidth
            size="small"
            style={{ marginBottom: 8 }}
          />
          <TextField
            label="Superscript"
            value={block.superscript || ""}
            onChange={(e) =>
              updateBlockField(block.refId, "superscript", e.target.value)
            }
            fullWidth
            size="small"
            style={{ marginBottom: 12 }}
          />

          <Divider style={{ marginBottom: 12 }} />

          {/* Sections within this block */}
          {(block.sections || []).map((sectionRefId, sectionIdx) => {
            const section = sectionsObj[sectionRefId];
            if (!section) {
              return null;
            }

            return (
              <SectionItem
                key={sectionRefId}
                section={section}
                sectionIdx={sectionIdx}
                sectionRefId={sectionRefId}
                block={block}
                totalSections={(block.sections || []).length}
                onMoveUp={() => moveSection(block.refId, sectionRefId, -1)}
                onMoveDown={() => moveSection(block.refId, sectionRefId, 1)}
                onRemove={() => removeSection(block.refId, sectionRefId)}
                onTitleChange={(val) => updateSectionTitle(sectionRefId, val)}
                onAddClass={() => addClassToSection(block.refId, sectionRefId)}
                onRemoveClass={(classRefId) => removeClassFromSection(block.refId, sectionRefId, classRefId)}
                onMoveClass={(classRefId, direction) => moveClassInSection(sectionRefId, classRefId, direction)}
                onUpdateClassId={(classRefId, classId) => updateClassId(sectionRefId, classRefId, classId)}
              />
            );
          })}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => addSection(block.refId)}
            style={{ marginTop: 8 }}
          >
            Add Section
          </Button>
        </Paper>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addBlock}
        style={{ marginTop: 8 }}
      >
        Add Block
      </Button>
    </div>
  );
}

/**
 * SectionItem — uses Collapse instead of Accordion to avoid
 * MUI v4 ButtonBase swallowing clicks on action buttons.
 */
function SectionItem({
  section,
  sectionIdx,
  sectionRefId,
  block,
  totalSections,
  onMoveUp,
  onMoveDown,
  onRemove,
  onTitleChange,
  onAddClass,
  onRemoveClass,
  onMoveClass,
  onUpdateClassId,
}) {
  const [expanded, setExpanded] = React.useState(true);

  return (
    <div style={{ border: "1px solid #e0e0e0", borderRadius: 4, marginBottom: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          background: "#fafafa",
        }}
      >
        <IconButton size="small" onClick={() => setExpanded((prev) => !prev)}>
          <ExpandMoreIcon
            fontSize="small"
            style={{
              transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s",
            }}
          />
        </IconButton>
        <Typography style={{ flex: 1, marginLeft: 4 }}>
          Section {sectionIdx + 1}: {section.title}
          {section.durationInSeconds > 0 && (
            <span style={{ color: "#888", marginLeft: 8 }}>
              ({Math.round(section.durationInSeconds / 60)} min)
            </span>
          )}
        </Typography>
        <IconButton
          size="small"
          onClick={onMoveUp}
          disabled={sectionIdx === 0}
        >
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onMoveDown}
          disabled={sectionIdx === totalSections - 1}
        >
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onRemove}
          color="secondary"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </div>
      <Collapse in={expanded}>
        <div style={{ padding: "12px 16px" }}>
          <TextField
            label="Section Title"
            value={section.title || ""}
            onChange={(e) => onTitleChange(e.target.value)}
            fullWidth
            size="small"
            style={{ marginBottom: 12 }}
          />

          {/* Classes within this section */}
          {(section.classes || []).map((cls, clsIdx) => (
            <div
              key={cls.refId}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <Typography
                variant="body2"
                style={{ marginRight: 8, minWidth: 60 }}
              >
                Class:
              </Typography>
              <TextField
                label="Class ID"
                type="number"
                value={cls.id || ""}
                onChange={(e) => onUpdateClassId(cls.refId, e.target.value)}
                size="small"
                style={{ width: 120, marginRight: 8 }}
              />
              <IconButton
                size="small"
                onClick={() => onMoveClass(cls.refId, -1)}
                disabled={clsIdx === 0}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onMoveClass(cls.refId, 1)}
                disabled={clsIdx === (section.classes || []).length - 1}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onRemoveClass(cls.refId)}
                color="secondary"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ))}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddClass}
          >
            Add Class
          </Button>
        </div>
      </Collapse>
    </div>
  );
}
